import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import Tabs from '../components/Tabs';
import { useHubExcelExport } from '../components/ExcelExportButton';
import { resolveTabs } from '../i18n/resolveTabs';
import ProfitLossPage from './ProfitLossPage';
import BalanceSheetPage from './BalanceSheetPage';
import AdjustmentsPage from './AdjustmentsPage';
import MappingPage from './MappingPage';
import BsMappingPage from './BsMappingPage';

/* Données — hub de SAISIE du réel de Finance (import Excel, saisie cellule par
   cellule, affectation des comptes, ajustements).

   Séparation par métier (décision 2026-08-09, achevée le 2026-08-18) :
   Rapports LIT, Données SAISIT, Paramètres STRUCTURE, Prévisionnel CONSTRUIT.
   Un rapport ne porte plus jamais de bouton d'édition — mettre à jour le réel
   se fait ICI, à une seule adresse. Les écrans à modes (barre de commande
   « Présenter | Éditer réel | Planifier ») sortent du menu : chaque pilule
   retrouve sa section métier.

     • Compte de résultat — le réel éditable (bouton ✏️ Éditer, import Excel,
       pinceau d'affectation) ; borné aux mois clôturés (`actualsOnly`).
     • Bilan              — même logique, avec notes et ajustements de poste.
     • Ajustements        — le journal des écritures d'ajustement (P&L et bilan).

   Le cash flow n'a pas d'onglet ici : il ne se saisit jamais, il se dérive du
   bilan. Sa catégorisation de lignes relève de la structure → Paramètres.

   Gabarit unique : <PageShell framed> + <Tabs> + sous-pages en mode embedded.
   Saisie au franc : pas de bascule d'unité (contrairement aux Rapports). */
const TAB_DEFS = [
  { id: 'pl', labelKey: 'finance:data.tabs.pl' },
  { id: 'bs', labelKey: 'finance:data.tabs.bs' },
  { id: 'adjustments', labelKey: 'finance:data.tabs.adjustments' },
  // Correspondance comptes → lignes de reporting (ex-hub Mapping, rapatrié ici
  // le 2026-08-18) : brancher la comptabilité est un geste de saisie du réel.
  { id: 'pl-mapping', labelKey: 'finance:input.tabs.pl-mapping' },
  { id: 'bs-mapping', labelKey: 'finance:input.tabs.bs-mapping' },
];

export default function FinancialsDataHub({ activeTenant, addToast, year, onYearChange }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('pl');
  const TABS = resolveTabs(TAB_DEFS, t);

  // `actualsOnly` borne l'affichage ; `loadedMonths` le règle sur le dernier mois
  // CHARGÉ (et non sur la clôture ni sur la version), pour préparer la clôture
  // avant de la poser (2026-10-16). Les mois à venir se pilotent dans le Prévisionnel.
  // Le Bilan garde ses douze mois, vides compris (`monthsFull`, 2026-10-16).
  const renderTab = (id) => {
    switch (id) {
      case 'pl':
        return <ProfitLossPage embedded actualsOnly loadedMonths activeTenant={activeTenant}
          addToast={addToast} year={year} onYearChange={onYearChange} />;
      case 'bs':
        return <BalanceSheetPage embedded actualsOnly monthsFull loadedMonths activeTenant={activeTenant}
          addToast={addToast} year={year} onYearChange={onYearChange} />;
      case 'adjustments':
        return <AdjustmentsPage embedded activeTenant={activeTenant}
          addToast={addToast} year={year} />;
      case 'pl-mapping':
        return <MappingPage embedded activeTenant={activeTenant} addToast={addToast} />;
      case 'bs-mapping':
        return <BsMappingPage embedded activeTenant={activeTenant} addToast={addToast} />;
      default: return null;
    }
  };

  // Export Excel — le hub entier d'un coup (1 onglet = 1 feuille), servi par
  // l'icône télécharger de la barre supérieure (registre `pageExports`). Pas de
  // PDF : un écran de saisie n'a pas de document d'impression.
  const excel = useHubExcelExport({
    tabs: TABS.map((tb) => ({ label: tb.label, render: () => renderTab(tb.id) })),
    fileBase: t('finance:data.title'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  const exportOptions = useMemo(
    () => [{ id: 'excel', label: 'Excel', run: excel.run }],
    [excel.run],
  );

  return (
    <PageShell framed kind="entry" exports={exportOptions}
               title={t('finance:data.title')}>
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <div className="page-shell__panel">
        {renderTab(tab)}
      </div>

      {excel.stage}
    </PageShell>
  );
}
